import React from 'react';
import {
  PageHeader,
  DataTable,
  Badge,
  Button,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  type Column,
} from '@redbax/ui-kit';
import type { AdminOrganization } from '../types/admin-types';
import { formatDate } from '@redbax/shared';
import {
  useAdminOrganizations,
  useApproveOrganization,
  useRejectOrganization,
  useSuspendOrganization,
  useActivateOrganization,
} from '../api/admin-hooks';

type BadgeVariant = 'success' | 'warning' | 'destructive' | 'secondary' | 'info' | 'default';

const statusVariantMap: Record<AdminOrganization['status'], BadgeVariant> = {
  Active: 'success',
  PendingApproval: 'warning',
  Suspended: 'destructive',
  Rejected: 'secondary',
  Provisioning: 'info',
  ProvisioningFailed: 'destructive',
};

const statusLabelMap: Record<AdminOrganization['status'], string> = {
  Active: 'Active',
  PendingApproval: 'Pending Approval',
  Suspended: 'Suspended',
  Rejected: 'Rejected',
  Provisioning: 'Provisioning',
  ProvisioningFailed: 'Provisioning Failed',
};

function OrgActionCell({ org }: { org: AdminOrganization }) {
  const approve = useApproveOrganization();
  const reject = useRejectOrganization();
  const suspend = useSuspendOrganization();
  const activate = useActivateOrganization();
  const [confirmAction, setConfirmAction] = React.useState<string | null>(null);

  const handleConfirm = () => {
    switch (confirmAction) {
      case 'approve':
        approve.mutate({ id: org.id });
        break;
      case 'reject':
        reject.mutate({ id: org.id });
        break;
      case 'suspend':
        suspend.mutate({ id: org.id });
        break;
      case 'activate':
        activate.mutate({ id: org.id });
        break;
    }
    setConfirmAction(null);
  };

  const actionLabel = confirmAction === 'approve' ? 'Approve'
    : confirmAction === 'reject' ? 'Reject'
    : confirmAction === 'suspend' ? 'Suspend'
    : confirmAction === 'activate' ? 'Activate'
    : '';

  return (
    <div className="flex items-center gap-2">
      {org.status === 'PendingApproval' && (
        <>
          <Button size="sm" onClick={() => setConfirmAction('approve')}>
            Approve
          </Button>
          <Button size="sm" variant="destructive" onClick={() => setConfirmAction('reject')}>
            Reject
          </Button>
        </>
      )}
      {org.status === 'Active' && (
        <Button size="sm" variant="destructive" onClick={() => setConfirmAction('suspend')}>
          Suspend
        </Button>
      )}
      {org.status === 'Suspended' && (
        <Button size="sm" onClick={() => setConfirmAction('activate')}>
          Activate
        </Button>
      )}

      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/80" onClick={() => setConfirmAction(null)} />
          <div className="relative z-50 w-full max-w-sm rounded-lg border border-border bg-surface p-6 shadow-lg">
            <h3 className="text-lg font-semibold text-text-primary">Confirm {actionLabel}</h3>
            <p className="mt-2 text-sm text-text-secondary">
              Are you sure you want to {actionLabel.toLowerCase()} <strong>{org.name}</strong>?
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmAction(null)}>
                Cancel
              </Button>
              <Button
                variant={confirmAction === 'reject' || confirmAction === 'suspend' ? 'destructive' : 'default'}
                onClick={handleConfirm}
              >
                {actionLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const columns: Column<AdminOrganization>[] = [
  { key: 'name', header: 'Name', sortable: true },
  { key: 'region', header: 'Region', sortable: true },
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    render: (row) => (
      <Badge variant={statusVariantMap[row.status]}>
        {statusLabelMap[row.status]}
      </Badge>
    ),
  },
  {
    key: 'createdAt',
    header: 'Created',
    sortable: true,
    render: (row) => formatDate(row.createdAt),
  },
  {
    key: 'actions' as keyof AdminOrganization,
    header: 'Actions',
    render: (row) => <OrgActionCell org={row} />,
  },
];

export function AdminOrganizationsPage() {
  const { data: organizations, isLoading } = useAdminOrganizations();

  return (
    <div>
      <PageHeader title="Organizations" description="Manage platform organizations" />
      <DataTable
        columns={columns}
        data={organizations ?? []}
        keyExtractor={(o) => o.id}
        loading={isLoading}
        emptyMessage="No organizations found"
      />
    </div>
  );
}
